import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Key, Search, Copy, Trash2, RotateCcw } from 'lucide-react';

/**
 * Filterable, searchable table of every USIC / CSIC subscription code.
 * Stateless wrt data fetching; all data and handlers come from the parent.
 */
export default function SubscriptionCodesTable({
    codes,
    searchTerm,
    setSearchTerm,
    filterType,
    setFilterType,
    filterStatus,
    setFilterStatus,
    getStatusBadge,
    copyCode,
    revokeCode,
    onRestoreClick,
}) {
    return (
        <Card>
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Key className="w-5 h-5 text-teal-600" />
                    Subscription Codes
                </CardTitle>
                <CardDescription>
                    Manage USIC and CSIC codes for users and organizations
                </CardDescription>
            </CardHeader>
            <CardContent>
                {/* Filters */}
                <div className="flex flex-wrap gap-4 mb-6">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                placeholder="Search codes, organizations..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="codes-search"
                            />
                        </div>
                    </div>
                    <Select value={filterType} onValueChange={setFilterType}>
                        <SelectTrigger className="w-[140px]" data-testid="filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="USIC">USIC</SelectItem>
                            <SelectItem value="CSIC">CSIC</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="w-[140px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Status</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="expired">Expired</SelectItem>
                            <SelectItem value="revoked">Revoked</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                <div className="border rounded-lg overflow-hidden">
                    {/* ── Desktop / tablet: full table ── */}
                    <table className="w-full text-sm hidden md:table" data-testid="codes-table-desktop">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="px-4 py-3 text-left font-medium text-slate-600">Code</th>
                                <th className="px-4 py-3 text-left font-medium text-slate-600">Type</th>
                                <th className="px-4 py-3 text-left font-medium text-slate-600">Organization</th>
                                <th className="px-4 py-3 text-left font-medium text-slate-600">Status</th>
                                <th className="px-4 py-3 text-left font-medium text-slate-600">Expires</th>
                                <th className="px-4 py-3 text-left font-medium text-slate-600">Used By</th>
                                <th className="px-4 py-3 text-right font-medium text-slate-600">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {codes.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-4 py-8 text-center text-slate-500">No codes found</td>
                                </tr>
                            ) : (
                                codes.map((code) => (
                                    <tr key={code.id} className="hover:bg-slate-50">
                                        <td className="px-4 py-3">
                                            <code className="bg-slate-100 px-2 py-1 rounded text-xs font-mono">{code.code}</code>
                                        </td>
                                        <td className="px-4 py-3">
                                            <Badge variant={code.code_type === 'USIC' ? 'default' : 'secondary'}>
                                                {code.code_type}
                                            </Badge>
                                        </td>
                                        <td className="px-4 py-3 text-slate-600">{code.organization_name || '-'}</td>
                                        <td className="px-4 py-3">{getStatusBadge(code)}</td>
                                        <td className="px-4 py-3 text-slate-600">
                                            {code.expires_at ? new Date(code.expires_at).toLocaleDateString() : 'Never'}
                                        </td>
                                        <td className="px-4 py-3 text-slate-600">
                                            <div>
                                                {code.used_by || '-'}
                                                {code.used_by_email && (
                                                    <span className="block text-xs text-slate-400">{code.used_by_email}</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button variant="ghost" size="sm" onClick={() => copyCode(code.code)} title="Copy code">
                                                    <Copy className="w-4 h-4" />
                                                </Button>
                                                {code.is_active ? (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => revokeCode(code.id)}
                                                        className="text-red-600 hover:text-red-700"
                                                        title="Revoke code"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => onRestoreClick(code)}
                                                        className="text-emerald-600 hover:text-emerald-700"
                                                        title="Restore code"
                                                    >
                                                        <RotateCcw className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>

                    {/* ── Mobile: stacked cards ── */}
                    <div className="md:hidden divide-y" data-testid="codes-list-mobile">
                        {codes.length === 0 ? (
                            <div className="px-4 py-8 text-center text-slate-500 text-sm">No codes found</div>
                        ) : (
                            codes.map((code) => (
                                <div key={code.id} className="p-4 bg-white hover:bg-slate-50 space-y-2">
                                    <div className="flex items-start justify-between gap-2">
                                        <code className="bg-slate-100 px-2 py-1 rounded text-xs font-mono break-all">{code.code}</code>
                                        <Badge variant={code.code_type === 'USIC' ? 'default' : 'secondary'} className="flex-shrink-0">
                                            {code.code_type}
                                        </Badge>
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                                        <div className="text-slate-500">Organization</div>
                                        <div className="text-slate-700 truncate">{code.organization_name || '-'}</div>
                                        <div className="text-slate-500">Status</div>
                                        <div>{getStatusBadge(code)}</div>
                                        <div className="text-slate-500">Expires</div>
                                        <div className="text-slate-700">{code.expires_at ? new Date(code.expires_at).toLocaleDateString() : 'Never'}</div>
                                        <div className="text-slate-500">Used By</div>
                                        <div className="text-slate-700 truncate">
                                            {code.used_by || '-'}
                                            {code.used_by_email && (
                                                <span className="block text-slate-400">{code.used_by_email}</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex justify-end gap-1 pt-1">
                                        <Button variant="outline" size="sm" onClick={() => copyCode(code.code)} className="h-8">
                                            <Copy className="w-3.5 h-3.5 mr-1" />Copy
                                        </Button>
                                        {code.is_active ? (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => revokeCode(code.id)}
                                                className="h-8 text-red-600 border-red-200"
                                            >
                                                <Trash2 className="w-3.5 h-3.5 mr-1" />Revoke
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => onRestoreClick(code)}
                                                className="h-8 text-emerald-600 border-emerald-200"
                                            >
                                                <RotateCcw className="w-3.5 h-3.5 mr-1" />Restore
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
